import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  Heading,
  Input,
  Label,
  ModalActions,
  ModalContent,
  ModalHeader,
  Multiselect,
  Option,
  Select,
} from '@alefeducation/ui-elements-library-core';
import { Times } from '@alefeducation/ui-elements-library-icons';
import FileInputButton from './FileInputButton';
import {
  StyledFormControlContainer,
  MandatorySpan,
  StyledRow,
  MultiSelectContainer,
  StyledHeaderContainer,
  StyledLabelContainer,
  StyledDescription,
  StyleCountryRow,
  StyledModal,
  CheckBoxContainer,
  DatePickerColumn,
} from './AvatarUploadModal.styled';
import { useStartUploadAvatarQuery } from './hooks/useStartUploadAvatarQuery';
import {
  ASSET_CATEGORY,
  EXPIRY_DATE,
  INCLUDE_FUTURE_ORGANISATION,
  GENDER,
  NEVER,
  ORGANISATION,
  SAVE,
  SELECT_ASSET_CATEGORY,
  SELECT_GENDER,
  SELECT_ORGANISATION,
  SELECT_TENANT,
  TENANT,
  UPLOAD_FILE,
  UPLOAD_FILE_DESCRIPTION,
} from '../../utils/constants';
import { isValidNumber } from '../../utils/Validators';
import { DatePickerButton } from '../DatePicker/DatePickerButton';
import { DatePicker } from '../DatePicker/DatePicker';
import {
  assetCategories,
  getAssetCategoryCode,
  getAssetCategoryName,
} from '../../utils/assetCategories';
import { genders as availableGenders } from '../../utils/gender';
import useAllOrganisationsAPI from '../RolesAndPermissions/CreateNewRole/hooks/useAllOrganisationsAPI';
import { useAvatarUploadContext } from '../AvatarUpload/context';
import { useGetAllTenants } from '../../apollo/queries/getAllTenants.queries';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AvatarUploadModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const date = new Date();
  const [files, setFiles] = useState<File[]>([]);
  const [starCost, setStarCost] = useState('');
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [assetCategory, setAssetCategory] = useState<string>('');
  const [genders, setGenders] = useState<string[]>([]);
  const [selectedOrganisations, setSelectedOrganisations] = useState<string[]>(
    [],
  );
  const [expiryDate, setExpiryDate] = useState<Date>(date);
  const [datePickerIsOpen, setDatePickerIsOpen] = useState<boolean>(false);
  const [expiryDateDisabled, setExpiryDateDisabled] = useState(true);
  const [includeFutureOrgs, setIncludeFutureOrgs] = useState(false);
  const { organisations: organisationsList } = useAllOrganisationsAPI();
  const { onRefresh } = useAvatarUploadContext();
  const [uploadAvatarsToS3, { loading }] = useStartUploadAvatarQuery(() => {
    onClose();
    onRefresh();
  });

  const { tenants } = useGetAllTenants();

  const organisations =
    selectedTenants.length === 0
      ? []
      : organisationsList.filter((org) =>
          selectedTenants.includes(org.tenantCode!),
        );

  const onKeyPress = (event: any) => {
    if (!isValidNumber(event.key)) {
      event.preventDefault();
    }
  };

  const handleFilesSelected = (selectedFiles: File[]) => {
    setFiles(selectedFiles);
  };

  const handleFilesRemoved = () => {
    setFiles([]);
  };

  const handleDateChange = (selected: Date) => {
    selected.setHours(23, 59, 59);
    setExpiryDate(selected);
  };

  const toggleDoNotCheck = () => setExpiryDateDisabled(!expiryDateDisabled);

  const getOrganizationName = (code: string) => {
    const org = organisationsList.find((o) => o.code === code);
    return org && org.name;
  };

  const validateFields = () => {
    return (
      files.length === 0 ||
      selectedTenants.length === 0 ||
      (!includeFutureOrgs && selectedOrganisations.length === 0) ||
      starCost === '' ||
      assetCategory === '' ||
      genders.length === 0 ||
      (!expiryDateDisabled && expiryDate === date)
    );
  };

  const onSave = () => {
    uploadAvatarsToS3({
      files,
      starCost: parseInt(starCost),
      tenants: selectedTenants,
      assetCategory,
      genders,
      organisation: selectedOrganisations,
      isEnabledForAllOrgs: includeFutureOrgs,
      expiryDate: (!expiryDateDisabled && expiryDate) || null,
    });
  };

  return (
    <StyledModal
      size="large"
      handleBackdropClick={() => {}}
      data-testid="add-avatars-modal"
      open={isOpen}
    >
      <ModalHeader>
        <Times
          role="icon"
          aria-label="cross"
          color="primary.40"
          height="14"
          width="14"
          cursor="pointer"
          onClick={() => onClose()}
        />
      </ModalHeader>
      <ModalContent>
        <StyledHeaderContainer>
          <Heading variant="heading-small" weight="bold" color="text.100">
            {UPLOAD_FILE}
          </Heading>
          <StyledDescription>{UPLOAD_FILE_DESCRIPTION}</StyledDescription>
        </StyledHeaderContainer>
        <StyledFormControlContainer>
          <FormControl>
            <div>
              <FileInputButton
                onFilesSelected={handleFilesSelected}
                onFilesRemoved={handleFilesRemoved}
              />
            </div>
          </FormControl>
        </StyledFormControlContainer>
        <StyledRow>
          <FormControl style={{ flex: 2 }}>
            <StyledLabelContainer>
              <MandatorySpan>* </MandatorySpan>
              <Label title="Star Cost" />
            </StyledLabelContainer>
            <Input
              onKeyPress={onKeyPress}
              name="star-cost"
              placeholder={'0'}
              value={starCost}
              onChange={(e) => {
                setStarCost(e.target.value);
              }}
              data-testid="star-cost"
            />
          </FormControl>
          <FormControl style={{ flex: 2 }}>
            <DatePickerColumn isDisabled={expiryDateDisabled}>
              <StyledLabelContainer>
                <Label title={EXPIRY_DATE} />
              </StyledLabelContainer>
              <div>
                <DatePicker
                  value={expiryDate}
                  isOpen={datePickerIsOpen}
                  onClose={() => setDatePickerIsOpen(false)}
                  onChange={(event) => handleDateChange(event)}
                  minDate={new Date()}
                >
                  <DatePickerButton
                    id="set-expiry-date"
                    value={expiryDate}
                    onClick={() =>
                      !expiryDateDisabled && setDatePickerIsOpen(true)
                    }
                    disabled={expiryDateDisabled}
                  />
                </DatePicker>
              </div>
            </DatePickerColumn>
          </FormControl>
          <CheckBoxContainer style={{ flex: 1.3 }}>
            <Checkbox
              data-testid="do-not-show-check"
              value="value"
              variant="default"
              checked={expiryDateDisabled}
              onClick={toggleDoNotCheck}
              onChange={() => {}}
            >
              {NEVER}
            </Checkbox>
          </CheckBoxContainer>
        </StyledRow>
        <StyleCountryRow>
          <StyledFormControlContainer style={{ flex: 2 }}>
            <StyledLabelContainer>
              <MandatorySpan>* </MandatorySpan>
              <Label title={TENANT} />
            </StyledLabelContainer>
            <MultiSelectContainer data-testid="select-container-tenant">
              <Multiselect
                showSelectAll
                selectAllLabel="All"
                textKey="name"
                selectedItems={selectedTenants.map((name) => ({
                  id: name,
                  name,
                }))}
                items={tenants.map((s) => ({ id: s, name: s }))}
                selectionChanged={(newTenants) => {
                  if (newTenants.length === 0) {
                    setSelectedOrganisations([]);
                  } else {
                    const [removedTenant] = selectedTenants.filter(
                      (tenant) => !newTenants.find((t) => t.id === tenant),
                    );
                    const organisations = organisationsList.filter(
                      (o) => o.tenantCode === removedTenant,
                    );

                    setSelectedOrganisations((prevOrg) =>
                      prevOrg.filter(
                        (org) => !organisations.find((o) => o.code === org),
                      ),
                    );
                  }
                  setSelectedTenants([...newTenants.map((c) => c.id)]);
                }}
                placeholder={SELECT_TENANT}
              />
            </MultiSelectContainer>
          </StyledFormControlContainer>
          <StyledFormControlContainer
            isDisabled={organisations.length === 0 || includeFutureOrgs}
            style={{ flex: 2 }}
          >
            <StyledLabelContainer>
              <MandatorySpan>* </MandatorySpan>
              <Label title={ORGANISATION} />
            </StyledLabelContainer>
            <MultiSelectContainer
              data-testid="organisation-multiselect"
              className="org-multiselect-container"
            >
              <Multiselect
                data-testid="select-org"
                showSelectAll
                selectAllLabel={
                  organisations.length === 0 ? 'Select Organisation' : 'All'
                }
                textKey="name"
                selectedItems={selectedOrganisations.map((s: string) => ({
                  id: s,
                  name: getOrganizationName(s)!,
                }))}
                unordered={true}
                items={organisations.map((s) => ({
                  id: s.code,
                  name: s.name,
                }))}
                selectionChanged={(newOrgs) => {
                  setSelectedOrganisations([...newOrgs.map((c) => c.id)]);
                }}
                placeholder={SELECT_ORGANISATION}
              />
            </MultiSelectContainer>
          </StyledFormControlContainer>
          <CheckBoxContainer style={{ flex: 1.3 }}>
            <Checkbox
              data-testid="do-not-show-check-org"
              value="value"
              variant="default"
              checked={includeFutureOrgs}
              onChange={() => {
                setIncludeFutureOrgs(!includeFutureOrgs);
                setSelectedOrganisations(organisations.map((s) => s.code));
              }}
            >
              {INCLUDE_FUTURE_ORGANISATION}
            </Checkbox>
          </CheckBoxContainer>
        </StyleCountryRow>
        <StyleCountryRow>
          <StyledFormControlContainer>
            <StyledLabelContainer>
              <MandatorySpan>* </MandatorySpan>
              <Label title={ASSET_CATEGORY} />
            </StyledLabelContainer>
            <MultiSelectContainer>
              <Select
                data-testid="select-asset-category"
                selectedOption={getAssetCategoryName(assetCategory) || ''}
                onSelectChange={(id, name) =>
                  setAssetCategory(getAssetCategoryCode(name)!)
                }
                placeholder={SELECT_ASSET_CATEGORY}
              >
                {assetCategories.map((asset) => (
                  <Option key={asset.code}>{asset.name}</Option>
                ))}
              </Select>
            </MultiSelectContainer>
          </StyledFormControlContainer>
          <StyledFormControlContainer>
            <StyledLabelContainer>
              <MandatorySpan>* </MandatorySpan>
              <Label title={GENDER} />
            </StyledLabelContainer>
            <MultiSelectContainer data-testid="select-gender">
              <Multiselect
                showSelectAll
                selectAllLabel="All"
                textKey="name"
                selectedItems={genders.map((code) => ({
                  id: code,
                  name: availableGenders.find((gender) => gender.code === code)!
                    .name,
                }))}
                selectionChanged={(selectedGenders) => {
                  setGenders([...selectedGenders.map((g) => g.id)]);
                }}
                items={availableGenders.map((gender) => ({
                  id: gender.code,
                  name: gender.name,
                }))}
                placeholder={SELECT_GENDER}
              />
            </MultiSelectContainer>
          </StyledFormControlContainer>
        </StyleCountryRow>
      </ModalContent>
      <ModalActions>
        <Button
          variant="secondary"
          onClick={() => {
            onClose();
          }}
          data-testid={'edit-org-cancel-button'}
        >
          Cancel
        </Button>
        <Button
          onClick={onSave}
          disabled={loading || validateFields()}
          data-testid={'add-org-save-button'}
        >
          {SAVE}
        </Button>
      </ModalActions>
    </StyledModal>
  );
};

export default AvatarUploadModal;
